import { ColorFieldConfiguratorProps } from './FieldConfigurators/ColorFieldConfigurator';
import { SimpleFieldConfiguratorProps } from './FieldConfigurators/SimpleFieldConfigurator';
import type {
  FlatInventoryItemConfiguratorOption,
  InventoryItemAttribute,
} from './types';
import { AttributeName } from './types';
import { flattenOptionAttributes } from './utils';

type ValidResult<T> = { success: true; value: T };
type ErrorResult = { success: false; reason: string };
type Result<T> = ValidResult<T> | ErrorResult;

type ColorPropValue = {
  type: 'color';
  options: ColorFieldConfiguratorProps['options'];
};
type ToggleFieldValue = {
  type: 'toggle';
  options: SimpleFieldConfiguratorProps['options'];
};
type SelectFieldValue = {
  type: 'select';
  options: SimpleFieldConfiguratorProps['options'];
};
type ReadOnlyFieldValue = {
  type: 'readonly';
  options: SimpleFieldConfiguratorProps['options'];
};

export type FieldData =
  | ColorPropValue
  | ToggleFieldValue
  | SelectFieldValue
  | ReadOnlyFieldValue;

const hasColor = <T extends Partial<Record<AttributeName.HexColor, string>>>(
  option: T
): option is T & Record<AttributeName.HexColor, string> => {
  return typeof option.hexColor === 'string';
};

// TODO[DEP-2254]: This is an interim solution for resolving defaultText, until we get an updated API
// core, which will have defaultText as part of the attribute's schema.
/**
 * This function will resolve a defaultText from an option. In the unlikely event of defaultText not being
 * available, we will fallBack to the option value's value. This will typically have non-localized,
 * non capitalized values like "red" instead of "Red", etc.
 */
const sanitizeDefaultText = <T extends FlatInventoryItemConfiguratorOption>(
  option: T,
  fallbackLogger: (option: T) => void
): T & { defaultText: string } => {
  if (option.defaultText) {
    return { ...option, defaultText: option.defaultText };
  }

  fallbackLogger(option);

  return { ...option, defaultText: option.value };
};

const validateColorData = <T extends FlatInventoryItemConfiguratorOption>(
  options: T[]
): Result<(T & Record<AttributeName.HexColor, string>)[]> => {
  const withColor = options.filter(hasColor);

  if (withColor.length !== options.length) {
    return { success: false, reason: 'items have missing color' };
  }

  return { success: true, value: withColor };
};

const EXPOSED_TOGGLE_OPTIONS_LENGTH_LIMIT = 10;

const validateExposedToggleFeasibility = <
  T extends Omit<FlatInventoryItemConfiguratorOption, AttributeName.Label> & {
    [AttributeName.Label]: string;
  },
>(
  options: T[]
): Result<(T & Record<AttributeName.Label, string>)[]> => {
  if (options.length > EXPOSED_TOGGLE_OPTIONS_LENGTH_LIMIT) {
    return {
      success: false,
      reason: `options exceed count limit of ${EXPOSED_TOGGLE_OPTIONS_LENGTH_LIMIT}`,
    };
  }
  return { success: true, value: options };
};

export const delegateField: (
  attributes: Array<InventoryItemAttribute>,
  defaultTextFallbackLogger: (
    option: FlatInventoryItemConfiguratorOption
  ) => void
) => Result<FieldData> = (attributes, defaultTextFallbackLogger) => {
  const flattenedOptions = attributes.map(flattenOptionAttributes);
  const withSanitizedDefaultText = flattenedOptions.map((option) =>
    sanitizeDefaultText(option, defaultTextFallbackLogger)
  );

  const colorOptionsResult = validateColorData(withSanitizedDefaultText);

  if (colorOptionsResult.success) {
    return {
      success: true,
      value: { type: 'color', options: colorOptionsResult.value },
    };
  }

  const exposedToggleButtonResult = validateExposedToggleFeasibility(
    withSanitizedDefaultText
  );

  if (exposedToggleButtonResult.success) {
    return {
      success: true,
      value: { type: 'toggle', options: exposedToggleButtonResult.value },
    };
  }

  return {
    success: true,
    value: { type: 'select', options: withSanitizedDefaultText },
  };
};
